import { useBunja } from 'bunja/react';

import { TradingLeaderboardTier } from '@flipster/apis/v2/trading-leaderboards/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { TradingLeaderboardEvent } from '@flipster/utils/segment/events/trading-leaderboard';

import { SegmentPage } from '../../../../misc/segment/segment-page';
import { entryPopupBlockBunja, entryPopupItemBunja } from '../../bunja';
import Shark from './assets/shark.svg';
import Starter from './assets/starter.svg';
import Whale from './assets/whale.svg';

const TierIcon = ({ tier }: { tier: TradingLeaderboardTier }) => {
  if (tier == 'ALL') return null;
  const Icon = {
    STARTER: Starter,
    WHALE: Whale,
    SHARK: Shark,
  }[tier];
  return <Icon width={120} height={120} />;
};

interface LeagueMainOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const LeagueMainOnboardingModal = ({ isOpen, onClose, onExit }: LeagueMainOnboardingModalProps) => {
  const router = useRouter();
  const { popup, markAsRead } = useBunja(entryPopupItemBunja);
  const { block } = useBunja(entryPopupBlockBunja);

  const handleClose = () => {
    markAsRead(popup.id);
    onClose();
  };

  if (popup.id !== 'WELCOME_TO_TIERED_LEAGUE') throw new Error('Popup ID mismatch');
  const tier = popup.data.tier;
  return (
    <>
      <SegmentPage
        name={TradingLeaderboardEvent.LeagueOnboardingView}
        context={{ tier, event_location: 'main_page' }}
      />
      <Modal isOpen={isOpen} onExit={onExit}>
        <Modal.Close onClose={handleClose} />
        <Modal.Content css={vstack.raw({ gap: 16 })}>
          <TierIcon tier={tier} />
          <div>
            <p
              className={css({
                textStyle: 'Text/M/Strong',
                color: 'content.accent.info',
                textAlign: 'center',
              })}
            >
              <FormattedMessage id='Market.r250922.First_trade_completed' />
            </p>
            <p
              className={css({
                textStyle: 'Title/M',
                textAlign: 'center',
                color: 'inverse.white',
                whiteSpace: 'pre-wrap',
              })}
            >
              {tier != 'ALL' && (
                <FormattedMessage
                  id={
                    (
                      {
                        STARTER: 'Market.r250925.Welcome_starter_league',
                        SHARK: 'Market.r250925.Welcome_shark_league',
                        WHALE: 'Market.r250925.Welcome_whale_league',
                      } as const
                    )[tier]
                  }
                />
              )}
            </p>
          </div>
          <p
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-3',
              textAlign: 'center',
            })}
          >
            <FormattedMessage id='Market.r250922.View_your_rank_rise' />
          </p>
        </Modal.Content>
        <Modal.Footer>
          <Button
            size='large'
            label={<FormattedMessage id='Common.r250922.View_my_rank' />}
            onClick={() => {
              segment.track(TradingLeaderboardEvent.LeagueOnboardingClick, {
                tier,
                event_location: 'main_page',
              });
              router.push(`/trading-leaderboard?tier=${tier}&interval=WEEKLY`);
              block('TIERED_LEAGUE_DESCRIPTION');
              onClose();
            }}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};
